import React from 'react'
import cn from 'classnames'

import LogoApple from 'media/logo-apple.svg?svgr-webpack'
import LogoAndroid from 'media/logo-android.svg?svgr-webpack'
import {
  appleRating,
  googleRating,
  averageRating,
} from 'lifesum-shared/reviews'
import StarRating from 'js/components/StarRating/StarRating'
import useBreakpoints from 'js/hooks/useBreakpoints'

import { android, appButtonUrl, device } from 'js/device'
import { trackInstallClick } from 'js/analytics'
import { FormattedMessage } from 'react-intl'
import Logo from 'js/components/Logo'
import { isSSR } from 'js/isSSR'
import { appIcon } from 'js/imageSources'

type GetAppButtonUrlProps = {
  appStoreUrl?: string
  googlePlayUrl?: string
}

const getAppButtonUrl = ({
  appStoreUrl,
  googlePlayUrl,
}: GetAppButtonUrlProps): string => {
  if (android && googlePlayUrl) {
    return googlePlayUrl
  }
  if (appStoreUrl) {
    return appStoreUrl
  }

  return appButtonUrl
}

type ReviewBlockProps = {
  type: 'google' | 'apple'
}

const ReviewBlock = ({ type }: ReviewBlockProps) => {
  const Logo = type === 'google' ? LogoAndroid : LogoApple
  return (
    <div className="mr-20">
      <span className="sr-only">
        {type === 'google' ? 'Google Play: ' : 'Apple AppStore: '}
      </span>
      <Logo className="mb-6 block" style={{ width: 24, height: 24 }} />
      <div className="flex">
        <div className="mr-4 xl:mr-6">
          <StarRating rating={type === 'google' ? googleRating : appleRating} />
        </div>
        <span className="mt-2 type-17">
          {type === 'google' ? googleRating : appleRating}
        </span>
      </div>
      <span className="sr-only">average rating</span>
    </div>
  )
}

function Ratings() {
  return (
    <div className="my-auto">
      <div className="hidden lg:flex">
        <ReviewBlock type="apple" />
        <ReviewBlock type="google" />
      </div>
      <div className="lg:hidden">
        <h2 className="font-demibold">Lifesum</h2>
        <StarRating rating={averageRating} />
      </div>
    </div>
  )
}

type Props = {
  showAppButtons: boolean
  cta: {
    buttonText: string
    webSignup: boolean
    appStoreUrl: string
    googlePlayUrl: string
  } | null
}

function Sticky({ cta, showAppButtons }: Props) {
  const { LG } = useBreakpoints()

  return (
    <div className="sticky left-0 right-0 top-0 z-20 bg-white px-0 py-16 shadow-elevated md:px-20">
      <div className="o-width-limiter flex items-center  justify-between">
        <div className="flex">
          {((cta && !cta.webSignup) || !LG) && (
            <img
              className={cn(`
                    mr-8 h-40 w-40 self-center rounded-8 sm:h-48
                    sm:w-48 md:mr-12 lg:mr-32 lg:h-64 lg:w-64
                  `)}
              src={`${appIcon}?auto=format&w=96`}
              alt="Lifesum app icon"
            />
          )}
          <Ratings />
        </div>
        {cta?.webSignup && (
          <Logo className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform lg:block" />
        )}
        {!isSSR && showAppButtons && (
          <a
            href={
              cta?.webSignup
                ? '/signup'
                : getAppButtonUrl({
                    appStoreUrl: cta?.appStoreUrl,
                    googlePlayUrl: cta?.googlePlayUrl,
                  })
            }
            className={cn(`
                  border-color-green ml-auto rounded-4
                  border-2 px-12 pb-4
                  pt-6 text-center font-demibold text-green sm:px-16 xl:px-20 xl:pb-6
                  xl:pt-8
                `)}
            onClick={() => trackInstallClick(device)}
          >
            {cta?.buttonText ? (
              cta.buttonText
            ) : (
              <FormattedMessage id="install_now" />
            )}
          </a>
        )}
      </div>
    </div>
  )
}

export default Sticky
